import React, { useEffect, useRef, useState } from "react";
import { Box, Tooltip, CircularProgress } from "@mui/material";
import { useTranslation } from "react-i18next";
import { TreatmentPlanAppointment } from "../../../../services/treatmentPlanService";
import {
  EditNoteIcon,
  MoveAppointmentIcon,
  TrashIcon,
} from "../../../../components/Icons";
import { colors } from "../../../../theme/colors";
import { deduplicateCdtCodesStructured } from "../../../../utils/cdtCodeUtils";
import { TreatmentStepChips, TreatmentStepEmptyValue } from "../TreatmentStepChips";
import {
  getAppointmentDuration,
  getAppointmentFee,
  getOptionLabel,
  getProcedureFee,
} from "../../../../utils/appointmentDisplayUtils";
import {
  AppointmentContainer,
  AppointmentHeader,
  AppointmentNameCell,
  AppointmentNumber,
  AppointmentName,
  AppointmentDuration,
  AppointmentBadge,
  AppointmentFee,
  AppointmentActions,
  ActionIconButton,
  DragHandle,
  AppointmentCdtSpacer,
  AppointmentColumnSpacer,
  AppointmentFeeRow,
  AppointmentFieldLabel,
  ProcedureCard,
  ProcedureTitle,
  ProcedureMobileField,
  ProcedureFieldLabel,
  ProcedureCdtValue,
  ProcedureFeeValue,
  TableActionsPlaceholder,
  CdtCode,
  AppointmentTimeframeRow,
  AppointmentLoadingOverlay,
} from "./TreatmentAppointment.styled";
import AppointmentTimeSeparator from "../StepTimeSeparator";

interface TreatmentAppointmentProps {
  appointment: TreatmentPlanAppointment;
  appointmentNumber: number;
  isLastAppointment?: boolean;
  showTooltip?: boolean;
  onEdit?: () => void;
  onDelete?: () => void;
  onDragStart?: (e: React.DragEvent) => void;
  isDraggable?: boolean;
  isDeleting?: boolean;
  showActionsColumn?: boolean;
}

const TreatmentAppointment: React.FC<TreatmentAppointmentProps> = ({
  appointment,
  appointmentNumber,
  isLastAppointment = false,
  showTooltip = true,
  onEdit,
  onDelete,
  onDragStart,
  isDraggable = false,
  isDeleting = false,
  showActionsColumn = true,
}) => {
  const { t } = useTranslation();
  const appointmentNameRef = useRef<HTMLElement>(null);
  const [isAppointmentNameTruncated, setIsAppointmentNameTruncated] =
    useState(false);

  const renderCode = (
    code: string,
    count: number,
    key: string,
    tooltipText?: string,
  ) => {
    const content = (
      <CdtCode key={key} component="span">
        {code}
        {count > 1 && <Box component="span"> x{count}</Box>}
      </CdtCode>
    );
    if (!tooltipText || !showTooltip) return content;
    return (
      <Tooltip key={key} title={tooltipText} arrow placement="top">
        <span>{content}</span>
      </Tooltip>
    );
  };

  const formatCdtCodes = (
    entries: ReturnType<typeof deduplicateCdtCodesStructured>,
    descriptionsByCode: Record<string, string>,
  ): React.ReactNode => {
    if (entries.length === 0) return null;

    const hasMultipliers = entries.some((e) => e.count > 1);
    const perRow = hasMultipliers && entries.length <= 2 ? 1 : 2;

    const rows: (typeof entries)[] = [];
    for (let i = 0; i < entries.length; i += perRow) {
      rows.push(entries.slice(i, i + perRow));
    }

    return (
      <>
        {rows.map((row, rowIdx) => (
          <React.Fragment key={rowIdx}>
            {rowIdx > 0 && "\n"}
            {row.map((entry) => (
              <React.Fragment key={entry.code}>
                {renderCode(
                  entry.code,
                  entry.count,
                  entry.code,
                  descriptionsByCode[entry.code],
                )}
              </React.Fragment>
            ))}
          </React.Fragment>
        ))}
      </>
    );
  };

  const optionLabel = getOptionLabel(appointment.conditionalPath);
  const totalFee = getAppointmentFee(appointment);
  const duration = getAppointmentDuration(appointment);
  const appointmentName =
    appointment.description?.trim() ||
    t("treatmentPlan.appointment", { number: appointmentNumber });

  useEffect(() => {
    const element = appointmentNameRef.current;
    if (!element) {
      setIsAppointmentNameTruncated(false);
      return;
    }

    const checkTruncation = () => {
      setIsAppointmentNameTruncated(
        element.scrollHeight > element.clientHeight + 1,
      );
    };

    checkTruncation();

    const resizeObserver = new ResizeObserver(checkTruncation);
    resizeObserver.observe(element);

    return () => {
      resizeObserver.disconnect();
    };
  }, [appointmentName]);

  const appointmentNameNode = (
    <Tooltip
      title={appointmentName}
      arrow
      placement="top"
      disableHoverListener={!isAppointmentNameTruncated}
    >
      <AppointmentName ref={appointmentNameRef}>
        {appointmentName}
      </AppointmentName>
    </Tooltip>
  );

  return (
    <AppointmentContainer isDeleting={isDeleting}>
      {isDeleting && (
        <AppointmentLoadingOverlay>
          <CircularProgress size={24} />
        </AppointmentLoadingOverlay>
      )}
      <AppointmentHeader showActionsColumn={showActionsColumn}>
        {isDraggable && onDragStart && (
          <DragHandle draggable onDragStart={onDragStart}>
            <MoveAppointmentIcon width="18px" height="18px" />
          </DragHandle>
        )}

        <AppointmentNameCell>
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              minWidth: 0,
              flex: 1,
            }}
          >
            <AppointmentNumber>#{appointmentNumber}</AppointmentNumber>
            {appointmentNameNode}
          </Box>
          {optionLabel && (
            <AppointmentBadge>
              {t("treatmentPlan.optionLabel", { label: optionLabel })}
            </AppointmentBadge>
          )}
          <AppointmentDuration>{duration}</AppointmentDuration>
        </AppointmentNameCell>

        <AppointmentCdtSpacer />
        <AppointmentColumnSpacer />
        <AppointmentColumnSpacer />

        <AppointmentFeeRow>
          <AppointmentFieldLabel>
            {t("treatmentPlan.fee")}
          </AppointmentFieldLabel>
          <AppointmentFee>${totalFee.toLocaleString("en-US")}</AppointmentFee>
        </AppointmentFeeRow>

        {showActionsColumn && (
          <AppointmentActions>
            {onEdit && (
              <ActionIconButton
                size="small"
                onClick={onEdit}
                disabled={isDeleting}
              >
                <EditNoteIcon width="18px" height="18px" />
              </ActionIconButton>
            )}
            {onDelete && (
              <ActionIconButton
                size="small"
                onClick={onDelete}
                disabled={isDeleting}
              >
                {isDeleting ? (
                  <CircularProgress
                    size={18}
                    sx={{ color: colors.status.error }}
                  />
                ) : (
                  <TrashIcon
                    width="18px"
                    height="18px"
                    fill={colors.status.error}
                  />
                )}
              </ActionIconButton>
            )}
          </AppointmentActions>
        )}
      </AppointmentHeader>

      {[...appointment.steps]
        .sort((a, b) => a.sortOrder - b.sortOrder)
        .map((step) => {
          const fee = getProcedureFee(step);
          const cdtEntries = deduplicateCdtCodesStructured(
            step.codeSuggestions,
          );
          const descriptionsByCode = step.codeSuggestions.reduce<
            Record<string, string>
          >((acc, item) => {
            if (item.code && item.description && !acc[item.code]) {
              acc[item.code] = item.description;
            }
            return acc;
          }, {});
          const cdtContent = formatCdtCodes(cdtEntries, descriptionsByCode);

          return (
            <ProcedureCard key={step.id} showActionsColumn={showActionsColumn}>
              <ProcedureTitle>{step.name}</ProcedureTitle>
              <ProcedureMobileField>
                <ProcedureFieldLabel>
                  {t("treatmentPlan.cdtCode")}
                </ProcedureFieldLabel>
                {cdtContent ? (
                  <ProcedureCdtValue>{cdtContent}</ProcedureCdtValue>
                ) : (
                  <TreatmentStepEmptyValue>
                    {t("treatmentPlan.noCode")}
                  </TreatmentStepEmptyValue>
                )}
              </ProcedureMobileField>
              <ProcedureMobileField>
                <ProcedureFieldLabel>
                  {t("treatmentPlan.tooth")}
                </ProcedureFieldLabel>
                <TreatmentStepChips
                  values={step.teeth ?? []}
                  emptyLabel={t("treatmentPlan.noTooth")}
                />
              </ProcedureMobileField>
              <ProcedureMobileField>
                <ProcedureFieldLabel>
                  {t("treatmentPlan.surface")}
                </ProcedureFieldLabel>
                <TreatmentStepChips
                  values={step.surfaces ?? []}
                  emptyLabel={t("treatmentPlan.noSurface")}
                />
              </ProcedureMobileField>
              <ProcedureMobileField>
                <ProcedureFieldLabel>
                  {t("treatmentPlan.fee")}
                </ProcedureFieldLabel>
                <ProcedureFeeValue>
                  ${fee.toLocaleString("en-US")}
                </ProcedureFeeValue>
              </ProcedureMobileField>
              {showActionsColumn && <TableActionsPlaceholder />}
            </ProcedureCard>
          );
        })}

      {!isLastAppointment && appointment.timeframeLabel && (
        <AppointmentTimeframeRow>
          <AppointmentTimeSeparator
            timeframeLabel={appointment.timeframeLabel}
          />
        </AppointmentTimeframeRow>
      )}
    </AppointmentContainer>
  );
};

export default TreatmentAppointment;
